import { usePlacementStore } from '@/stores/placement'
import { useAdSizeConfigStore } from '@/stores/adSizeConfig'
import { FabricObject, FabricText, type Canvas } from 'fabric'

// Getter functions for text limits from config store
export function getMaximumAllowedWidth(): number {
  return useAdSizeConfigStore().maximumAllowedWidth
}

export function getMinimumAllowedWidth(): number {
  return useAdSizeConfigStore().minimumAllowedWidth
}

export function getEstimatedSingleLineHeight(): number {
  return useAdSizeConfigStore().estimatedSingleLineHeight
}

export const updateTextObject = (fabricObject: any, properties: any) => {
  fabricObject.set({ ...properties })
  fabricObject.initDimensions()
  fabricObject.setCoords()
}

export const applyAutofitToTextObject = (
  textObj: any,
  maxFontSize: number,
  maxTextWidth: number = -1,
) => {
  if (!textObj || !textObj.textLines) return

  const currentFontSize = Number(textObj.fontSize) || 24
  let autofitFontSize = currentFontSize

  if (maxTextWidth === -1) {
    maxTextWidth = textObj.width
  }

  // If text width excludes the max width then reduce the font size to reduce the width
  if (textObj.getScaledWidth() > maxTextWidth) {
    autofitFontSize /= 2
    textObj.set({ fontSize: autofitFontSize })
    textObj.set({ width: maxTextWidth - 1 })
    textObj.initDimensions()
  }

  // Increase font size if there's room
  while (textObj.textLines.length <= (textObj.maxLines ?? 2) && autofitFontSize < maxFontSize) {
    autofitFontSize += 1
    textObj.set({ fontSize: autofitFontSize })
    textObj.initDimensions()

    // If text width excludes the max width then reduce the width
    if (textObj.getScaledWidth() > maxTextWidth) {
      autofitFontSize -= 1
      textObj.set({ fontSize: autofitFontSize })
      textObj.set({ width: maxTextWidth })
      textObj.initDimensions()
      break
    }
  }

  // Decrease font size if text doesn't fit
  while (textObj.textLines.length > (textObj.maxLines ?? 2) && autofitFontSize > 1) {
    autofitFontSize -= 1
    textObj.set({ fontSize: autofitFontSize })
    textObj.initDimensions()
  }
}

export const resetHeadlineSubheadlines = (fabricCanvas: Canvas) => {
  const placementStore = usePlacementStore()
  const objects = fabricCanvas.getObjects()
  const originalObjects = placementStore.selectedPublishedVariant?.canvasJson.objects || []

  const headlineOriginal = originalObjects.find((obj: any) => obj.layerName === 'Headline')
  const subheadlineOriginal = originalObjects.find((obj: any) => obj.layerName === 'SubHeadline')

  const currentHeadline: any = objects.find((obj: any) => obj.layerName === 'Headline')
  const currentSubheadline = objects.find((obj: any) => obj.layerName === 'SubHeadline')

  // Copy basic properties
  if (headlineOriginal && currentHeadline) {
    currentHeadline.set('fontSize', headlineOriginal.fontSize)
    currentHeadline.set('top', headlineOriginal.top)
    currentHeadline.set('left', headlineOriginal.left)
    currentHeadline.set('width', headlineOriginal.width)
    currentHeadline.set('maxLines', headlineOriginal.maxLines)
    currentHeadline.previousHeight = getEstimatedSingleLineHeight()
    currentHeadline.setCoords()
  }

  if (subheadlineOriginal && currentSubheadline) {
    currentSubheadline.set('fontSize', subheadlineOriginal.fontSize)
    currentSubheadline.set('top', subheadlineOriginal.top)
    currentSubheadline.set('left', subheadlineOriginal.left)
    currentSubheadline.set('width', subheadlineOriginal.width)
    currentSubheadline.set('maxLines', subheadlineOriginal.maxLines)
    currentSubheadline.setCoords()
  }
}

export const applyMaxLinesAndVerticalOffset = (
  headline: any,
  subheadline: FabricText,
  previousHeight: number = getEstimatedSingleLineHeight(), // Default height for headline with a single line
  newWidth: number = -1,
) => {
  const lines = (headline as any)._textLines?.length || 1
  const maxLines = lines > 1 ? 2 : 1
  const lineHeight = lines == 1 ? 1.16 : 1
  headline.set({ maxLines: maxLines, lineHeight: lineHeight })
  applyAutofitToTextObject(headline, 64, newWidth)
  headline.setCoords()
  headline.initDimensions()
  headline.previousHeight = headline.height

  const newHeight = headline.getBoundingRect().height
  const heightDiff = newHeight - previousHeight
  const offset = heightDiff / 2

  headline.set({ top: headline.top + offset })
  subheadline.set({ top: subheadline.top + offset })
}

export const adjustHeadlineOnCollision = (
  product: FabricObject,
  headline: any,
  collision: boolean = false,
  subheadline: FabricText,
) => {
  const buffer = 10
  const MINIMUM_WIDTH = getMinimumAllowedWidth()
  const MAXIMUM_WIDTH = getMaximumAllowedWidth()

  const productBounds = product.getBoundingRect()
  const headlineBounds = headline.getBoundingRect()
  const previousHeight = headline.previousHeight
    ? headline.previousHeight
    : getEstimatedSingleLineHeight()
  const rightEdgeOfHeadline = headlineBounds.left + headline.width
  const collideDiff = Math.abs(productBounds.left - rightEdgeOfHeadline)
  const spaceAvailable = productBounds.left - headlineBounds.left

  let newWidth

  if (collision) {
    // Collision: shrink headline
    const calculatedWidth = headline.width - collideDiff - buffer
    newWidth = Math.max(calculatedWidth, MINIMUM_WIDTH)
  } else {
    // No collision: expand headline
    newWidth = Math.min(spaceAvailable - buffer, MAXIMUM_WIDTH)
  }
  // Set new width if any of the following:
  // - Collision is true (we must shrink to avoid overlap)
  // - New width is greater than current (safe to expand)
  // - Not enough space between headline and product (less than 10px buffer)
  if (collision || newWidth > headline.width || collideDiff < 10) {
    headline.set({ width: newWidth })
  } else {
    newWidth = -1
  }

  headline.setCoords()
  headline.initDimensions()

  applyMaxLinesAndVerticalOffset(headline, subheadline, previousHeight, newWidth)
}

export const adjustSubheadlineOnCollision = (
  product: FabricObject,
  subheadline: FabricText,
  collision: boolean = false,
) => {
  const buffer = 10
  const MINIMUM_WIDTH = getMinimumAllowedWidth()
  const MAXIMUM_WIDTH = getMaximumAllowedWidth()

  const productBounds = product.getBoundingRect()
  const subheadlineBounds = subheadline.getBoundingRect()

  const rightEdge = subheadlineBounds.left + subheadlineBounds.width
  const collideDiff = Math.abs(productBounds.left - rightEdge)
  const spaceAvailable = productBounds.left - subheadlineBounds.left

  let newWidth

  if (collision) {
    const calculatedWidth = subheadline.width - collideDiff - buffer
    newWidth = Math.max(calculatedWidth, MINIMUM_WIDTH)
  } else {
    newWidth = Math.min(spaceAvailable - buffer, MAXIMUM_WIDTH)
    if (newWidth <= subheadline.width && collideDiff >= 10) {
      applyAutofitToTextObject(subheadline, 44)
      return // No expand needed
    }
  }

  subheadline.set({
    width: newWidth,
    maxLines: 2, // always fixed
  })
  subheadline.setCoords()
  subheadline.initDimensions()

  applyAutofitToTextObject(subheadline, 44, newWidth)
}

export const reflowTextAfterScaling = (canvas: any, product: FabricObject) => {
  const objects = canvas.getObjects()
  const headlineObj = objects.find((obj: any) => obj.layerName.includes('Headline'))
  const subheadlineObj = objects.find((obj: any) => obj.layerName.includes('SubHeadline'))

  product.setCoords()
  headlineObj.set({ scaleX: 1, scaleY: 1 })
  headlineObj.setCoords()
  subheadlineObj.set({ scaleX: 1, scaleY: 1 })
  subheadlineObj.setCoords()

  if (headlineObj) {
    const hasCollision = product.intersectsWithObject(headlineObj)
    adjustHeadlineOnCollision(product, headlineObj, hasCollision, subheadlineObj)
  }

  if (subheadlineObj) {
    const hasCollision = product.intersectsWithObject(subheadlineObj)
    adjustSubheadlineOnCollision(product, subheadlineObj, hasCollision)
  }
}

export const reflowTextBasedOnCollisions = (canvas: any, sortedProducts: FabricObject[]) => {
  const objects = canvas.getObjects()
  const headline = objects.find((obj: any) => obj.layerName.includes('Headline'))
  const subheadline = objects.find((obj: any) => obj.layerName.includes('SubHeadline'))

  if (!headline || !subheadline || sortedProducts.length === 0) return null

  // Update coordinates
  headline.setCoords()
  subheadline.setCoords()
  sortedProducts.forEach((p) => p.setCoords())

  const leftProduct = sortedProducts[0]
  const middleProduct = sortedProducts[1]

  // Step 1: Always reflow middle product if present
  if (middleProduct) {
    reflowTextAfterScaling(canvas, middleProduct)
    headline.setCoords()
    subheadline.setCoords()
    sortedProducts.forEach((p) => p.setCoords())
  }

  // Step 2: Always reflow left product
  if (leftProduct) {
    reflowTextAfterScaling(canvas, leftProduct)
    headline.setCoords()
    subheadline.setCoords()
  }
}

export const handleUSToUKEnglish = (headlineText: string) => {
  let updatedText = headlineText

  // Handle "upto" to "Up to"/"up to"
  if (updatedText.toLowerCase().startsWith('upto')) {
    updatedText = updatedText.replace(/^upto/i, 'Up to')
  } else {
    updatedText = updatedText.replace(/upto/gi, 'up to')
  }

  // Replace "flavor" with "flavour" (case-insensitive)
  updatedText = updatedText.replace(/\bflavor\b/gi, (match) =>
    match[0] === match[0].toUpperCase() ? 'Flavour' : 'flavour',
  )

  updatedText = updatedText.replace(/\bflavors\b/gi, (match) =>
    match[0] === match[0].toUpperCase() ? 'Flavours' : 'flavours',
  )

  return updatedText
}

export const USkeywords = ['upto', 'flavor', 'flavors']
